/**
 * KONFIGURACJA JAJEK — BIOME MEADOW (MVP)
 *
 * 4 jajka w sklepie Meadow:
 *   - egg-starter    (50 coins)   — głównie commons, trochę uncommon, śladowo rare
 *   - egg-basic      (250 coins)  — commons + uncommon + rare + szczypta epic/legendary
 *   - egg-rare       (2500 coins) — głównie uncommon/rare/epic
 *   - egg-legendary  (25k coins)  — bez commons, uncommon/rare/epic/legendary
 *
 * Drop tabele są budowane funkcją `buildDropTable()` z wag PER RARITY.
 * Wagi pochodzą wprost z balansu §12 GAME_DESIGN.md i są automatycznie dzielone
 * równo pomiędzy pety tego samego rarity (z PET_IDS_BY_RARITY w pets.config.ts).
 *
 * Żeby przekręcić balans jajka — zmieniasz JEDNĄ liczbę w `weightsByRarity`,
 * a nie 60 wpisów ręcznie.
 *
 * HUGE/TITANIC (warianty rozmiarowe): pierwszy taki pet — `pet-meadow-hamburger-cat`
 * (Kotburger, rarity=mythic) — jest wpięty w `egg-legendary` jako huge
 * (hugeChance=0.003 + hugeDropTable + pity) ORAZ titanic (titanicChance=0.0002 +
 * titanicDropTable; titanic rzadszy/lepszy, sprawdzany przed huge). Pozostałe
 * jajka mają huge/titanicChance=0. Wzorzec na kolejnego:
 *   1. Dodaj PetDef w pets.config.ts (rarity=mythic, tag 'huge-only' jeśli pet
 *      ma istnieć WYŁĄCZNIE jako wariant rozmiarowy; istniejący pet — bez tagu).
 *   2. Wpisz { petId, variant:'huge'|'titanic', weight } do huge/titanicDropTable.
 *   3. Ustaw huge/titanicChance na docelową wartość (titanic << huge).
 *   4. Zregeneruj serwer: npx tsx scripts/gen-server-eggs.ts
 *      + dodaj wpis w server/api/src/game-config/pet-power.ts i pet-rarity.ts.
 *
 * Pety w grze są dostępne WYŁĄCZNIE z tych jajek — patrz GAME_DESIGN §5.4.
 */

import halloween from '../../server/api/src/game-config/halloween.config.json';
import type { EggDef, EggDropEntry } from '@/domain/types/egg';
import type { Rarity, Variant, ID } from '@/domain/types/common';
import { PET_IDS_BY_RARITY, PETS_BY_ID } from './pets.config';
import { EGG_BASE_COSTS, PITY_DEFAULTS } from './balance.config';
import { isEventActive } from './events.config';

const BIOME_MEADOW = 'biome-meadow';
const BIOME_MOUNTAIN = 'biome-mountain';
const BIOME_STARWARS = 'biome-starwars';
const BIOME_PIXEL = 'biome-pixel';
const BIOME_OCEAN = 'biome-ocean';
const BIOME_FOOD = 'biome-food';
const BIOME_ATLANTIS = 'biome-atlantis';
const BIOME_SCHOOL = 'biome-school';
const BIOME_ARCTIC = 'biome-arctic';
const BIOME_FLAGS_EUROPE = 'biome-flags-europe';
const BIOME_PUSZCZA = 'biome-puszcza';
const BIOME_EVENT_BEACH = 'biome-event-capibara-beach';

/**
 * Buduje listę EggDropEntry z definicji wag per-rarity, **filtrując po biomie**.
 *
 * Dla każdego rarity bierze listę petów tego rarity NALEŻĄCYCH do podanego biomu
 * i rozdziela wagę równo między nich. Bez biome filter mountain-egg dropowałby
 * meadow pety i odwrotnie — patrz sesja 26 (dodanie biomu Mountain).
 *
 * Pet jest "z danego biomu" gdy `pet.origin.biomeId === biomeId`. Pety bez
 * origin.biomeId (legacy) są zaliczane do `biome-meadow` jako fallback.
 *
 * Przykład (meadow):
 *   buildDropTable({ common: 800, uncommon: 180, rare: 20 }, 'biome-meadow')
 *   → tylko 25 meadow commonów, każdy weight=32.
 *
 * Jeśli waga nie dzieli się równo — używamy `Math.round`. Suma może odbiegać
 * o ±N od podanej. Akceptowalne dla wag.
 */
function buildDropTable(
  weightsByRarity: Partial<Record<Rarity, number>>,
  biomeId: ID,
  variant: Variant = 'normal',
): EggDropEntry[] {
  const result: EggDropEntry[] = [];
  for (const rarity of Object.keys(weightsByRarity) as Rarity[]) {
    const totalWeight = weightsByRarity[rarity] ?? 0;
    const allOfRarity = PET_IDS_BY_RARITY[rarity] ?? [];
    const petIds = allOfRarity.filter((petId) => {
      const def = PETS_BY_ID[petId];
      // 'huge-only' pety (np. pet-meadow-hamburger-cat) NIGDY nie wchodzą do
      // normalnych pul — wypadają wyłącznie jako wariant 'huge' przez
      // hugeDropTable. Bez tego dedykowany huge zaśmiecałby zwykły drop swojej
      // rarity (np. legendary owl/pony).
      if (def?.tags?.includes('huge-only')) return false;
      const petBiome = def?.origin?.biomeId ?? BIOME_MEADOW;
      return petBiome === biomeId;
    });
    if (totalWeight <= 0 || petIds.length === 0) continue;
    const perPet = Math.max(1, Math.round(totalWeight / petIds.length));
    for (const petId of petIds) {
      result.push({ petId, variant, weight: perPet });
    }
  }
  return result;
}

export const EGGS: EggDef[] = [
  {
    id: 'egg-starter',
    name: { pl: 'Jajko Startowe', en: 'Starter Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.starter },
    biomeId: BIOME_MEADOW,
    hugeChance: 0,
    hugeDropTable: [],
    dropTable: buildDropTable({
      common: 800,
      uncommon: 180,
      rare: 20,
      // epic: 0, legendary: 0
    }, BIOME_MEADOW),
    art: { textureKey: 'egg_starter', glowColor: '#9BE07F' },
    enabled: true,
  },
  {
    id: 'egg-basic',
    name: { pl: 'Jajko Łąkowe', en: 'Meadow Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.basic },
    biomeId: BIOME_MEADOW,
    hugeChance: 0,                  // TODO: ustaw 0.0003 gdy dodasz huge'y
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 500,
      uncommon: 350,
      rare: 120,
      epic: 28,
      legendary: 2,
    }, BIOME_MEADOW),
    art: { textureKey: 'egg_basic', glowColor: '#7FC8E0' },
    enabled: true,
  },
  {
    id: 'egg-rare',
    name: { pl: 'Jajko Rzadkie', en: 'Rare Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.rare },
    biomeId: BIOME_MEADOW,
    hugeChance: 0,                  // TODO: ustaw 0.001 gdy dodasz huge'y
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 200,
      uncommon: 450,
      rare: 280,
      epic: 65,
      legendary: 5,
    }, BIOME_MEADOW),
    art: { textureKey: 'egg_rare', glowColor: '#C77FE0' },
    enabled: true,
  },
  {
    id: 'egg-legendary',
    name: { pl: 'Jajko Legendarne', en: 'Legendary Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.legendary },
    biomeId: BIOME_MEADOW,
    // HUGE biome 1: Kotburger. 0.003 = ~1/333 rolli; pity (300) gwarantuje
    // huge'a najpóźniej w 300. Podnieś wartość, by łatwiej trafiać w testach.
    hugeChance: 0.003,
    hugeDropTable: [
      { petId: 'pet-meadow-hamburger-cat', variant: 'huge', weight: 1 },
    ],
    // TITANIC biome 1: ten sam Kotburger, ale apex (×100 mocy). Rzadszy od huge —
    // 0.0002 = ~1/5000. Sprawdzany PRZED huge (titanic > huge). Bez pity (zbyt rzadki).
    titanicChance: 0.0002,
    titanicDropTable: [
      { petId: 'pet-meadow-hamburger-cat', variant: 'titanic', weight: 1 },
    ],
    pity: { thresholdRolls: 300, targetVariant: 'huge' }, // szybsza gwarancja w top-jajku
    guarantees: {
      // hurtowe otwieranie 10× → minimum 1 epic w paczce
      minRarityOnBulk: { bulkSize: 10, minRarity: 'epic' },
    },
    dropTable: buildDropTable({
      // common: 0 — bez śmieci w top-jajku
      uncommon: 200,
      rare: 500,
      epic: 250,
      legendary: 50,
    }, BIOME_MEADOW),
    art: { textureKey: 'egg_legendary', glowColor: '#FFD24A' },
    enabled: true,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MOUNTAIN BIOME (tier 2) — ceny ×10 vs meadow, drop tables filtrowane do
  // 60 mountain petów. PNG w `assets/eggs/egg_mountain_*.png`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'egg-mountain-starter',
    name: { pl: 'Kamienne Jajko', en: 'Stone Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.starter * 10 },         // 500
    biomeId: BIOME_MOUNTAIN,
    hugeChance: 0,
    hugeDropTable: [],
    dropTable: buildDropTable({
      common: 800,
      uncommon: 180,
      rare: 20,
    }, BIOME_MOUNTAIN),
    art: { textureKey: 'egg_mountain_starter', glowColor: '#A8B0B3' },
    enabled: true,
  },
  {
    id: 'egg-mountain-basic',
    name: { pl: 'Śnieżne Jajko', en: 'Snowcap Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.basic * 10 },           // 2_500
    biomeId: BIOME_MOUNTAIN,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 500,
      uncommon: 350,
      rare: 120,
      epic: 28,
      legendary: 2,
    }, BIOME_MOUNTAIN),
    art: { textureKey: 'egg_mountain_basic', glowColor: '#BFEFFF' },
    enabled: true,
  },
  {
    id: 'egg-mountain-rare',
    name: { pl: 'Kryształowe Jajko', en: 'Ice Crystal Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.rare * 10 },            // 25_000
    biomeId: BIOME_MOUNTAIN,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 200,
      uncommon: 450,
      rare: 280,
      epic: 65,
      legendary: 5,
    }, BIOME_MOUNTAIN),
    art: { textureKey: 'egg_mountain_rare', glowColor: '#7FE6FF' },
    enabled: true,
  },
  {
    id: 'egg-mountain-legendary',
    name: { pl: 'Jajko Szczytów Aurory', en: 'Aurora Peak Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.legendary * 10 },       // 250_000
    biomeId: BIOME_MOUNTAIN,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: 300, targetVariant: 'huge' },
    guarantees: {
      minRarityOnBulk: { bulkSize: 10, minRarity: 'epic' },
    },
    dropTable: buildDropTable({
      uncommon: 200,
      rare: 500,
      epic: 250,
      legendary: 50,
    }, BIOME_MOUNTAIN),
    art: { textureKey: 'egg_mountain_legendary', glowColor: '#BFA7FF' },
    enabled: true,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // DZIKI ZACHÓD BIOME (tier 3, dawniej Star Wars — ID zostają). Ceny ×100 vs meadow. 4 jajka: starter/basic/jedi/
  // force. egg-starwars-force dropuje 2 bossy (Szeryf, Biały Bizonek) jako huge LUB titanic.
  // PNG w `assets/eggs/egg_starwars_*.png`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'egg-starwars-starter',
    name: { pl: 'Jajko Prerii', en: 'Prairie Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.starter * 100 },         // 5_000
    biomeId: BIOME_STARWARS,
    hugeChance: 0,
    hugeDropTable: [],
    dropTable: buildDropTable({
      common: 800,
      uncommon: 180,
      rare: 20,
    }, BIOME_STARWARS),
    art: { textureKey: 'egg_starwars_starter', glowColor: '#E6C68F' },
    enabled: true,
  },
  {
    id: 'egg-starwars-basic',
    name: { pl: 'Jajko Kowbojskie', en: 'Cowboy Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.basic * 100 },           // 25_000
    biomeId: BIOME_STARWARS,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 500,
      uncommon: 350,
      rare: 120,
      epic: 28,
      legendary: 2,
    }, BIOME_STARWARS),
    art: { textureKey: 'egg_starwars_basic', glowColor: '#FF8C00' },
    enabled: true,
  },
  {
    id: 'egg-starwars-jedi',
    name: { pl: 'Jajko Szeryfa', en: 'Sheriff Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.rare * 100 },            // 250_000
    biomeId: BIOME_STARWARS,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 200,
      uncommon: 450,
      rare: 280,
      epic: 65,
      legendary: 5,
    }, BIOME_STARWARS),
    art: { textureKey: 'egg_starwars_jedi', glowColor: '#7FE6FF' },
    enabled: true,
  },
  {
    id: 'egg-starwars-force',
    name: { pl: 'Jajko Gorączki Złota', en: 'Gold Rush Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.legendary * 100 },       // 2_500_000
    biomeId: BIOME_STARWARS,
    // HUGE/TITANIC bossy: Szeryf + Biały Bizonek. Huge 0.003 (~1/333, pity 300 backstop),
    // titanic 0.0002 (~1/5000, sprawdzany przed huge). Każdy boss równe wagi.
    hugeChance: 0.003,
    hugeDropTable: [
      { petId: 'pet-starwars-darth-vader', variant: 'huge', weight: 1 },
      { petId: 'pet-starwars-grogu', variant: 'huge', weight: 1 },
    ],
    titanicChance: 0.0002,
    titanicDropTable: [
      { petId: 'pet-starwars-darth-vader', variant: 'titanic', weight: 1 },
      { petId: 'pet-starwars-grogu', variant: 'titanic', weight: 1 },
    ],
    pity: { thresholdRolls: 300, targetVariant: 'huge' },
    guarantees: {
      minRarityOnBulk: { bulkSize: 10, minRarity: 'epic' },
    },
    dropTable: buildDropTable({
      uncommon: 200,
      rare: 500,
      epic: 250,
      legendary: 50,
    }, BIOME_STARWARS),
    art: { textureKey: 'egg_starwars_force', glowColor: '#FFD700' },
    enabled: true,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PIXEL WORLD BIOME (tier 4) — ceny ×1000 vs meadow. 4 jajka: starter/basic/
  // arcade/glitch. egg-pixel-glitch dropuje 2 bossy (Król Glitchów, 8-bit Bohater)
  // jako huge LUB titanic. PNG w `assets/eggs/egg_pixel_*.png`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'egg-pixel-starter',
    name: { pl: 'Jajko 8-bit', en: 'Pixel Starter Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.starter * 1000 },        // 50_000
    biomeId: BIOME_PIXEL,
    hugeChance: 0,
    hugeDropTable: [],
    dropTable: buildDropTable({
      common: 800,
      uncommon: 180,
      rare: 20,
    }, BIOME_PIXEL),
    art: { textureKey: 'egg_pixel_starter', glowColor: '#7BE06A' },
    enabled: true,
  },
  {
    id: 'egg-pixel-basic',
    name: { pl: 'Jajko Retro', en: 'Retro Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.basic * 1000 },          // 250_000
    biomeId: BIOME_PIXEL,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 500,
      uncommon: 350,
      rare: 120,
      epic: 28,
      legendary: 2,
    }, BIOME_PIXEL),
    art: { textureKey: 'egg_pixel_basic', glowColor: '#5AA9E6' },
    enabled: true,
  },
  {
    id: 'egg-pixel-arcade',
    name: { pl: 'Jajko Arcade', en: 'Arcade Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.rare * 1000 },           // 2_500_000
    biomeId: BIOME_PIXEL,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 200,
      uncommon: 450,
      rare: 280,
      epic: 65,
      legendary: 5,
    }, BIOME_PIXEL),
    art: { textureKey: 'egg_pixel_arcade', glowColor: '#FF2D9B' },
    enabled: true,
  },
  {
    id: 'egg-pixel-glitch',
    name: { pl: 'Jajko Glitch', en: 'Glitch Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.legendary * 1000 },      // 25_000_000
    biomeId: BIOME_PIXEL,
    // HUGE/TITANIC bossy: Król Glitchów + 8-bit Bohater. Huge 0.003 (~1/333,
    // pity 300 backstop), titanic 0.0002 (~1/5000, sprawdzany przed huge).
    hugeChance: 0.003,
    hugeDropTable: [
      { petId: 'pet-pixel-glitch_king', variant: 'huge', weight: 1 },
      { petId: 'pet-pixel-hero', variant: 'huge', weight: 1 },
    ],
    titanicChance: 0.0002,
    titanicDropTable: [
      { petId: 'pet-pixel-glitch_king', variant: 'titanic', weight: 1 },
      { petId: 'pet-pixel-hero', variant: 'titanic', weight: 1 },
    ],
    pity: { thresholdRolls: 300, targetVariant: 'huge' },
    guarantees: {
      minRarityOnBulk: { bulkSize: 10, minRarity: 'epic' },
    },
    dropTable: buildDropTable({
      uncommon: 200,
      rare: 500,
      epic: 250,
      legendary: 50,
    }, BIOME_PIXEL),
    art: { textureKey: 'egg_pixel_glitch', glowColor: '#A36DFF' },
    enabled: true,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // OCEAN BIOME (tier 5) — ceny ×10000 vs meadow. 4 jajka: starter/basic/deep/
  // abyss. egg-ocean-abyss dropuje 2 bossów (Wieloryb + Orka) jako huge LUB titanic.
  // PNG w `assets/eggs/egg_ocean_*.png`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'egg-ocean-starter',
    name: { pl: 'Jajko Muszla', en: 'Shell Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.starter * 10000 },        // 500_000
    biomeId: BIOME_OCEAN,
    hugeChance: 0,
    hugeDropTable: [],
    dropTable: buildDropTable({
      common: 800,
      uncommon: 180,
      rare: 20,
    }, BIOME_OCEAN),
    art: { textureKey: 'egg_ocean_starter', glowColor: '#7FE0E0' },
    enabled: true,
  },
  {
    id: 'egg-ocean-basic',
    name: { pl: 'Jajko Rafowe', en: 'Reef Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.basic * 10000 },          // 2_500_000
    biomeId: BIOME_OCEAN,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 500,
      uncommon: 350,
      rare: 120,
      epic: 28,
      legendary: 2,
    }, BIOME_OCEAN),
    art: { textureKey: 'egg_ocean_basic', glowColor: '#4FC8E0' },
    enabled: true,
  },
  {
    id: 'egg-ocean-deep',
    name: { pl: 'Jajko Głębinowe', en: 'Deep Sea Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.rare * 10000 },           // 25_000_000
    biomeId: BIOME_OCEAN,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 200,
      uncommon: 450,
      rare: 280,
      epic: 65,
      legendary: 5,
    }, BIOME_OCEAN),
    art: { textureKey: 'egg_ocean_deep', glowColor: '#2E6E9E' },
    enabled: true,
  },
  {
    id: 'egg-ocean-abyss',
    name: { pl: 'Jajko Otchłani', en: 'Abyss Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.legendary * 10000 },      // 250_000_000
    biomeId: BIOME_OCEAN,
    // HUGE/TITANIC bossy: Wieloryb + Orka (równe wagi). Huge 0.003 (~1/333, pity
    // 300 backstop), titanic 0.0002 (~1/5000, sprawdzany przed huge).
    hugeChance: 0.003,
    hugeDropTable: [
      { petId: 'pet-ocean-whale', variant: 'huge', weight: 1 },
      { petId: 'pet-ocean-orca', variant: 'huge', weight: 1 },
    ],
    titanicChance: 0.0002,
    titanicDropTable: [
      { petId: 'pet-ocean-whale', variant: 'titanic', weight: 1 },
      { petId: 'pet-ocean-orca', variant: 'titanic', weight: 1 },
    ],
    pity: { thresholdRolls: 300, targetVariant: 'huge' },
    guarantees: {
      minRarityOnBulk: { bulkSize: 10, minRarity: 'epic' },
    },
    dropTable: buildDropTable({
      uncommon: 200,
      rare: 500,
      epic: 250,
      legendary: 50,
    }, BIOME_OCEAN),
    art: { textureKey: 'egg_ocean_abyss', glowColor: '#1E3A8C' },
    enabled: true,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // FOOD BIOME (tier 6) — ceny ×100000 vs meadow. 4 jajka: starter/basic/deluxe/
  // feast. egg-food-feast dropuje 2 bossy (Piesburger + Lodówka) jako huge LUB
  // titanic. PNG w `assets/eggs/egg_food_*.png`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'egg-food-starter',
    name: { pl: 'Jajko Przekąska', en: 'Snack Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.starter * 100000 },        // 5_000_000
    biomeId: BIOME_FOOD,
    hugeChance: 0,
    hugeDropTable: [],
    dropTable: buildDropTable({
      common: 800,
      uncommon: 180,
      rare: 20,
    }, BIOME_FOOD),
    art: { textureKey: 'egg_food_starter', glowColor: '#FFD24A' },
    enabled: true,
  },
  {
    id: 'egg-food-basic',
    name: { pl: 'Jajko Fast Food', en: 'Fast Food Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.basic * 100000 },          // 25_000_000
    biomeId: BIOME_FOOD,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 500,
      uncommon: 350,
      rare: 120,
      epic: 28,
      legendary: 2,
    }, BIOME_FOOD),
    art: { textureKey: 'egg_food_basic', glowColor: '#FF7A33' },
    enabled: true,
  },
  {
    id: 'egg-food-deluxe',
    name: { pl: 'Jajko Deluxe', en: 'Deluxe Meal Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.rare * 100000 },           // 250_000_000
    biomeId: BIOME_FOOD,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 200,
      uncommon: 450,
      rare: 280,
      epic: 65,
      legendary: 5,
    }, BIOME_FOOD),
    art: { textureKey: 'egg_food_deluxe', glowColor: '#E8553D' },
    enabled: true,
  },
  {
    id: 'egg-food-feast',
    name: { pl: 'Jajko Uczta', en: 'Feast Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.legendary * 100000 },      // 2_500_000_000
    biomeId: BIOME_FOOD,
    // HUGE/TITANIC bossy: Piesburger + Lodówka (równe wagi). Huge 0.003 (~1/333,
    // pity 300 backstop), titanic 0.0002 (~1/5000, sprawdzany przed huge).
    hugeChance: 0.003,
    hugeDropTable: [
      { petId: 'pet-food-hamburger-dog', variant: 'huge', weight: 1 },
      { petId: 'pet-food-fridge', variant: 'huge', weight: 1 },
    ],
    titanicChance: 0.0002,
    titanicDropTable: [
      { petId: 'pet-food-hamburger-dog', variant: 'titanic', weight: 1 },
      { petId: 'pet-food-fridge', variant: 'titanic', weight: 1 },
    ],
    pity: { thresholdRolls: 300, targetVariant: 'huge' },
    guarantees: {
      minRarityOnBulk: { bulkSize: 10, minRarity: 'epic' },
    },
    dropTable: buildDropTable({
      uncommon: 200,
      rare: 500,
      epic: 250,
      legendary: 50,
    }, BIOME_FOOD),
    art: { textureKey: 'egg_food_feast', glowColor: '#F4A623' },
    enabled: true,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ATLANTYDA BIOME (tier 7) — ceny ×1_000_000 vs meadow (×10 vs food). 4 jajka:
  // starter/basic/deluxe/royal. egg-atlantis-royal dropuje 3 bossy (Posejdon +
  // Tryton + Kraken) jako huge LUB titanic. PNG w `assets/eggs/egg_atlantis_*.png`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'egg-atlantis-starter',
    name: { pl: 'Jajko Muszla', en: 'Shell Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.starter * 1_000_000 },        // 50_000_000
    biomeId: BIOME_ATLANTIS,
    hugeChance: 0,
    hugeDropTable: [],
    dropTable: buildDropTable({
      common: 800,
      uncommon: 180,
      rare: 20,
    }, BIOME_ATLANTIS),
    art: { textureKey: 'egg_atlantis_starter', glowColor: '#5FD6D0' },
    enabled: true,
  },
  {
    id: 'egg-atlantis-basic',
    name: { pl: 'Jajko Głębin', en: 'Deep Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.basic * 1_000_000 },          // 250_000_000
    biomeId: BIOME_ATLANTIS,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 500,
      uncommon: 350,
      rare: 120,
      epic: 28,
      legendary: 2,
    }, BIOME_ATLANTIS),
    art: { textureKey: 'egg_atlantis_basic', glowColor: '#2E9EB0' },
    enabled: true,
  },
  {
    id: 'egg-atlantis-deluxe',
    name: { pl: 'Jajko Koralowe', en: 'Coral Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.rare * 1_000_000 },           // 2_500_000_000
    biomeId: BIOME_ATLANTIS,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 200,
      uncommon: 450,
      rare: 280,
      epic: 65,
      legendary: 5,
    }, BIOME_ATLANTIS),
    art: { textureKey: 'egg_atlantis_deluxe', glowColor: '#E8B84A' },
    enabled: true,
  },
  {
    id: 'egg-atlantis-royal',
    name: { pl: 'Jajko Królewskie', en: 'Royal Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.legendary * 1_000_000 },      // 25_000_000_000
    biomeId: BIOME_ATLANTIS,
    // HUGE/TITANIC bossy: Posejdon + Tryton + Kraken (równe wagi). Huge 0.003
    // (~1/333, pity 300 backstop), titanic 0.0002 (~1/5000, sprawdzany przed huge).
    hugeChance: 0.003,
    hugeDropTable: [
      { petId: 'pet-atlantis-poseidon', variant: 'huge', weight: 1 },
      { petId: 'pet-atlantis-triton', variant: 'huge', weight: 1 },
      { petId: 'pet-atlantis-kraken', variant: 'huge', weight: 1 },
    ],
    titanicChance: 0.0002,
    titanicDropTable: [
      { petId: 'pet-atlantis-poseidon', variant: 'titanic', weight: 1 },
      { petId: 'pet-atlantis-triton', variant: 'titanic', weight: 1 },
      { petId: 'pet-atlantis-kraken', variant: 'titanic', weight: 1 },
    ],
    pity: { thresholdRolls: 300, targetVariant: 'huge' },
    guarantees: {
      minRarityOnBulk: { bulkSize: 10, minRarity: 'epic' },
    },
    dropTable: buildDropTable({
      uncommon: 200,
      rare: 500,
      epic: 250,
      legendary: 50,
    }, BIOME_ATLANTIS),
    art: { textureKey: 'egg_atlantis_royal', glowColor: '#8E6BD8' },
    enabled: true,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SZKOŁA BIOME (tier 8) — ceny ×10_000_000 vs meadow (×10 vs atlantis).
  // 4 jajka: starter/basic/honors/graduation. egg-school-graduation dropuje
  // 3 bossy (Król Ołówek + Tornister Olbrzym + Wielki Dzwonek) jako huge LUB
  // titanic. PNG w `assets/eggs/egg_school_*.png`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'egg-school-starter',
    name: { pl: 'Jajko Pierwszaka', en: 'First-Grader Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.starter * 10_000_000 },     // 500_000_000
    biomeId: BIOME_SCHOOL,
    hugeChance: 0,
    hugeDropTable: [],
    dropTable: buildDropTable({
      common: 800,
      uncommon: 180,
      rare: 20,
    }, BIOME_SCHOOL),
    art: { textureKey: 'egg_school_starter', glowColor: '#7FC8F0' },
    enabled: true,
  },
  {
    id: 'egg-school-basic',
    name: { pl: 'Jajko Tornistrowe', en: 'Backpack Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.basic * 10_000_000 },       // 2_500_000_000
    biomeId: BIOME_SCHOOL,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 500,
      uncommon: 350,
      rare: 120,
      epic: 28,
      legendary: 2,
    }, BIOME_SCHOOL),
    art: { textureKey: 'egg_school_basic', glowColor: '#E8A33D' },
    enabled: true,
  },
  {
    id: 'egg-school-honors',
    name: { pl: 'Jajko Piątkowe', en: 'Honors Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.rare * 10_000_000 },        // 25_000_000_000
    biomeId: BIOME_SCHOOL,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 200,
      uncommon: 450,
      rare: 280,
      epic: 65,
      legendary: 5,
    }, BIOME_SCHOOL),
    art: { textureKey: 'egg_school_honors', glowColor: '#F2C24A' },
    enabled: true,
  },
  {
    id: 'egg-school-graduation',
    name: { pl: 'Jajko Absolwenta', en: 'Graduation Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.legendary * 10_000_000 },   // 250_000_000_000
    biomeId: BIOME_SCHOOL,
    // HUGE/TITANIC bossy: Król Ołówek + Tornister Olbrzym + Wielki Dzwonek
    // (równe wagi). Huge 0.003 (~1/333, pity 300 backstop), titanic 0.0002
    // (~1/5000, sprawdzany przed huge) — identycznie jak egg-atlantis-royal.
    hugeChance: 0.003,
    hugeDropTable: [
      { petId: 'pet-school-pencil-king', variant: 'huge', weight: 1 },
      { petId: 'pet-school-giant-backpack', variant: 'huge', weight: 1 },
      { petId: 'pet-school-great-bell', variant: 'huge', weight: 1 },
    ],
    titanicChance: 0.0002,
    titanicDropTable: [
      { petId: 'pet-school-pencil-king', variant: 'titanic', weight: 1 },
      { petId: 'pet-school-giant-backpack', variant: 'titanic', weight: 1 },
      { petId: 'pet-school-great-bell', variant: 'titanic', weight: 1 },
    ],
    pity: { thresholdRolls: 300, targetVariant: 'huge' },
    guarantees: {
      minRarityOnBulk: { bulkSize: 10, minRarity: 'epic' },
    },
    dropTable: buildDropTable({
      uncommon: 200,
      rare: 500,
      epic: 250,
      legendary: 50,
    }, BIOME_SCHOOL),
    art: { textureKey: 'egg_school_graduation', glowColor: '#7A5CC8' },
    enabled: true,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ARKTYKA (tier 9) — ceny ×100_000_000 vs meadow (×10 vs School).
  // Go-live 2026-09-04: komplet PNG/WebP jest gotowy, a wszystkie cztery jajka
  // są aktywne razem z biomem i serwerowym RNG. Jajko Korony Polarnej ma pięciu
  // bossów Huge/Titanic z briefu, z równymi wagami.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'egg-arctic-frost',
    name: { pl: 'Jajko Szronu', en: 'Frost Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.starter * 100_000_000 },     // 5_000_000_000
    biomeId: BIOME_ARCTIC,
    hugeChance: 0,
    hugeDropTable: [],
    dropTable: buildDropTable({
      common: 800,
      uncommon: 180,
      rare: 20,
    }, BIOME_ARCTIC),
    art: { textureKey: 'egg_arctic_frost', glowColor: '#BDEFFF' },
    enabled: true,
  },
  {
    id: 'egg-arctic-pack-ice',
    name: { pl: 'Jajko Kry', en: 'Pack Ice Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.basic * 100_000_000 },       // 25_000_000_000
    biomeId: BIOME_ARCTIC,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 500,
      uncommon: 350,
      rare: 120,
      epic: 28,
      legendary: 2,
    }, BIOME_ARCTIC),
    art: { textureKey: 'egg_arctic_pack_ice', glowColor: '#67CFE8' },
    enabled: true,
  },
  {
    id: 'egg-arctic-aurora',
    name: { pl: 'Jajko Zorzy', en: 'Aurora Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.rare * 100_000_000 },        // 250_000_000_000
    biomeId: BIOME_ARCTIC,
    hugeChance: 0,
    hugeDropTable: [],
    pity: { thresholdRolls: PITY_DEFAULTS.hugeFloor, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 200,
      uncommon: 450,
      rare: 280,
      epic: 65,
      legendary: 5,
    }, BIOME_ARCTIC),
    art: { textureKey: 'egg_arctic_aurora', glowColor: '#78E6C8' },
    enabled: true,
  },
  {
    id: 'egg-arctic-polar-crown',
    name: { pl: 'Jajko Korony Polarnej', en: 'Polar Crown Egg' },
    cost: { currency: 'coins', amount: EGG_BASE_COSTS.legendary * 100_000_000 },   // 2_500_000_000_000
    biomeId: BIOME_ARCTIC,
    // Narwal + Humbak + Pingwin + Orka + Kostka Lodu. Huge ≈1/333, Titanic
    // ≈1/5000; pity 300 i bulk 10→minimum epic jak w top-jajkach poprzednich światów.
    hugeChance: 0.003,
    hugeDropTable: [
      { petId: 'pet-arctic-narwhal', variant: 'huge', weight: 1 },
      { petId: 'pet-arctic-humpback-whale', variant: 'huge', weight: 1 },
      { petId: 'pet-arctic-penguin', variant: 'huge', weight: 1 },
      { petId: 'pet-arctic-orca', variant: 'huge', weight: 1 },
      { petId: 'pet-arctic-ice-cube', variant: 'huge', weight: 1 },
    ],
    titanicChance: 0.0002,
    titanicDropTable: [
      { petId: 'pet-arctic-narwhal', variant: 'titanic', weight: 1 },
      { petId: 'pet-arctic-humpback-whale', variant: 'titanic', weight: 1 },
      { petId: 'pet-arctic-penguin', variant: 'titanic', weight: 1 },
      { petId: 'pet-arctic-orca', variant: 'titanic', weight: 1 },
      { petId: 'pet-arctic-ice-cube', variant: 'titanic', weight: 1 },
    ],
    pity: { thresholdRolls: 300, targetVariant: 'huge' },
    guarantees: {
      minRarityOnBulk: { bulkSize: 10, minRarity: 'epic' },
    },
    dropTable: buildDropTable({
      uncommon: 200,
      rare: 500,
      epic: 250,
      legendary: 50,
    }, BIOME_ARCTIC),
    art: { textureKey: 'egg_arctic_polar_crown', glowColor: '#A98AF4' },
    enabled: true,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ŚWIAT FLAG — Biom edukacyjny (kontynent Europa). Wstęp do biomu DARMOWY;
  // samo jajko tanie (30 monet) — edukacyjna miniatura ekonomii competitive.
  // Lekkie rarity (common/uncommon/rare), bez epic/legendary/mythic i bez
  // huge/titanic.
  // Dedykowany asset atlasowego jajka: `assets/eggs/egg_flags_europe.webp`.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'egg-flags-europe',
    name: { pl: 'Jajko Flag Europy', en: 'Europe Flags Egg' },
    cost: { currency: 'coins', amount: 30 },
    biomeId: BIOME_FLAGS_EUROPE,
    hugeChance: 0,
    hugeDropTable: [],
    dropTable: buildDropTable({
      common: 700,
      uncommon: 250,
      rare: 50,
    }, BIOME_FLAGS_EUROPE),
    art: { textureKey: 'egg_flags_europe', glowColor: '#4F8FD8' },
    enabled: true,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PUSZCZA NIEPOŁOMICKA — biom edukacyjny nr 2. DWA jajka, bo biom ma dwie
  // bardzo różne półki wartości:
  //
  //   • „Jajko Runa Leśnego" (30 monet) — grzyby i rośliny (common/uncommon/rare).
  //     Wejście dla świeżego konta, dokładnie jak Jajko Flag Europy: dziecko ma
  //     od pierwszej minuty co zbierać i czego się uczyć.
  //
  //   • „Jajko Legend Puszczy" (25 000 monet) — 20 ZWIERZĄT, wszystkie `legendary`,
  //     + Dąb Szypułkowy (epic) + Żubr (mythic, HUGE-only).
  //     ⚠ DLACZEGO TAK DROGO: `legendary` = 50 💎 przy sprzedaży do NPC
  //     (trading-plaza.config.json → npcSellPrice), a `epic` = 10 💎. Gdyby
  //     zwierzęta wpadły do jajka za 30 monet, biom edukacyjny stałby się
  //     drukarką gemów (bulk hatch ×12 jednym kliknięciem). Przy 25 000 monet
  //     oczekiwany zysk to ~78 💎 na jajko (wliczając huge Żubra), czyli
  //     0,0031 💎 za monetę — praktycznie kurs „Jajka Legendarnego" Łąki
  //     (81,9 💎 / 25 000 monet = 0,0033). Zmieniając cenę albo wagi, PRZELICZ
  //     ten kurs ponownie: suma po dropTable z npcSellPrice(rarity) plus
  //     hugeChance × npcSellPrice(mythic) × 100, podzielone przez cenę.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'egg-puszcza-runo',
    name: { pl: 'Jajko Runa Leśnego', en: 'Forest Floor Egg' },
    cost: { currency: 'coins', amount: 30 },
    biomeId: BIOME_PUSZCZA,
    hugeChance: 0,
    hugeDropTable: [],
    // Bez epic/legendary: dąb, zwierzęta i żubr są wyłącznie w jajku legend.
    dropTable: buildDropTable({
      common: 620,
      uncommon: 300,
      rare: 80,
    }, BIOME_PUSZCZA),
    art: { textureKey: 'egg_puszcza_runo', glowColor: '#7BA05B' },
    enabled: true,
  },
  {
    id: 'egg-puszcza-legend',
    name: { pl: 'Jajko Legend Puszczy', en: 'Forest Legends Egg' },
    cost: { currency: 'coins', amount: 25_000 },
    biomeId: BIOME_PUSZCZA,
    // HUGE biomu: Żubr — jedyny mythic Puszczy, wypada TYLKO jako wariant huge
    // (tag 'huge-only' trzyma go poza normalną pulą). 0.002 ≈ 1/500; pity 300
    // gwarantuje huge'a najpóźniej w 300 rollu (wzorzec Kotburgera z Łąki, ale
    // przy tej samej cenie jajka szansa jest o 1/3 niższa — huge mythic to
    // 250 💎 × 100, czyli 50 z 78 💎 oczekiwanego zysku tego jajka).
    hugeChance: 0.002,
    hugeDropTable: [
      { petId: 'pet-puszcza-zubr', variant: 'huge', weight: 1 },
    ],
    // TITANIC: ten sam Żubr jako apex (×100 mocy). 0.0001 ≈ 1/10000, sprawdzany
    // PRZED huge. Bez pity — ma być legendą opowiadaną na podwórku.
    titanicChance: 0.0001,
    titanicDropTable: [
      { petId: 'pet-puszcza-zubr', variant: 'titanic', weight: 1 },
    ],
    pity: { thresholdRolls: 300, targetVariant: 'huge' },
    guarantees: {
      // hurtowe otwieranie 10× → minimum 1 epic (Dąb) w paczce
      minRarityOnBulk: { bulkSize: 10, minRarity: 'epic' },
    },
    // common/uncommon: 0 — bez grzybowego śmiecia w drogim jajku.
    // legendary 500/1000 = 50% → komplet 20 zwierząt to realny cel kolekcjonerski
    // (~130 wyklut na wszystkie gatunki), a nie loteria raz na tysiąc.
    dropTable: buildDropTable({
      rare: 300,
      epic: 200,
      legendary: 500,
    }, BIOME_PUSZCZA),
    art: { textureKey: 'egg_puszcza_legend', glowColor: '#C8A34A' },
    enabled: true,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // EVENT „WAKACJE CAPIBAR" (EV-2b) — biom plaży. Sklep eventu widoczny tylko gdy
  // event trwa (gate w getActiveEvents). 2 jajka:
  //   - egg-event-capibara-summer       — za 25 muszelek (event_shells)
  //   - egg-event-capibara-summer-gold  — za muszelki ORAZ gemy naraz (costAll),
  //                                        lepsze szanse HUGE/TITANIC
  // Roster 62 (rozbudowa 2026-06-29): 51 capibar NORMALNEJ puli (22C/15U/9R/4E/1L)
  // + 10 HUGE-only (cała dziesiątka PLAKATU/ALBUMU → hugeDropTable) + 1 TITANIC
  // `orange` (→ titanicDropTable). buildDropTable(BEACH) bierze tylko 51 normalnych
  // (10 huge i orange wykluczone tagiem 'huge-only'). Zwykłe jajko = pełne 51;
  // Złote = premium BEZ common (29: 15U/9R/4E/1L). Brak mythic-roll (Słońce jest huge).
  // Liczby = balans MVP, strojone w configu po testach syna (E-OQ2).
  // ⚠ GO-LIVE: jajka są `enabled:true`, ale sklep eventu pokazuje je tylko w oknie
  //   eventu (event off → niedostępne). PNG: assets/eggs/egg_event_capibara_summer(_gold).webp.
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'egg-event-capibara-summer',
    name: { pl: 'Jajko Wakacyjne', en: 'Summer Egg' },
    cost: { currency: 'event_shells', amount: 25 },
    biomeId: BIOME_EVENT_BEACH,
    eventId: 'event-capibara-summer',
    // HUGE: wszystkie 10 capibar z PLAKATU/ALBUMU (decyzja właściciela 2026-06-29 —
    // „te 10 MUSZĄ być huge"). Pula daje LOSOWĄ z 10 → komplet albumu = problem
    // kolekcjonera (event celowo trudny). 0.004 ≈ 1/250; pity 200 = backstop
    // (gwarancja JAKIEGOŚ huge co 200 wyklut, nie konkretnego).
    hugeChance: 0.004,
    hugeDropTable: [
      { petId: 'pet-event-capibara-summer-soldier', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-pirate', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-ninja', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-astronaut', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-knight', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-dragon', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-cyborg', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-superhero', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-king', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-sun', variant: 'huge', weight: 1 },
    ],
    // TITANIC: Capibara Pomarańcza — JEDYNY titanic eventu (apex, capibara z połówką
    // pomarańczy). 0.0001 ≈ 1/10000, sprawdzany przed huge (titanic > huge). Bez pity
    // (skrajnie rzadki, jak titanic w biomach).
    titanicChance: 0.0001,
    titanicDropTable: [
      { petId: 'pet-event-capibara-summer-orange', variant: 'titanic', weight: 1 },
    ],
    pity: { thresholdRolls: 200, targetVariant: 'huge' },
    dropTable: buildDropTable({
      common: 500,
      uncommon: 350,
      rare: 120,
      epic: 28,
      legendary: 2,
    }, BIOME_EVENT_BEACH),
    art: { textureKey: 'egg_event_capibara_summer', glowColor: '#3FC5C9' },
    enabled: true,
  },
  {
    id: 'egg-event-capibara-summer-gold',
    name: { pl: 'Złote Jajko Wakacyjne', en: 'Golden Summer Egg' },
    // Złote = premium. Płacisz OBYDWOMA walutami NARAZ: 75 muszelek I 100 gemów
    // (decyzja właściciela — nie „albo/albo"). `costAll` (AND) ma pierwszeństwo nad
    // cost/costOptions. `cost` zostaje jako wartość kanoniczna/fallback (muszelki).
    cost: { currency: 'event_shells', amount: 75 },
    costAll: [
      { currency: 'event_shells', amount: 75 },
      { currency: 'gems', amount: 100 },
    ],
    biomeId: BIOME_EVENT_BEACH,
    eventId: 'event-capibara-summer',
    // Lepsze szanse niż zwykłe jajko (premium). Pity szybsze (100). Pula huge = te
    // same 10 capibar plakatu (losowa); titanic 8× lepszy niż w zwykłym jajku.
    hugeChance: 0.02,        // ≈ 1/50
    hugeDropTable: [
      { petId: 'pet-event-capibara-summer-soldier', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-pirate', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-ninja', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-astronaut', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-knight', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-dragon', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-cyborg', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-superhero', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-king', variant: 'huge', weight: 1 },
      { petId: 'pet-event-capibara-summer-sun', variant: 'huge', weight: 1 },
    ],
    titanicChance: 0.0008,   // ≈ 1/1250 (premium, lepszy niż zwykłe 1/10000)
    titanicDropTable: [
      { petId: 'pet-event-capibara-summer-orange', variant: 'titanic', weight: 1 },
    ],
    pity: { thresholdRolls: 100, targetVariant: 'huge' },
    guarantees: {
      // hurtowe 10× → minimum 1 epic w paczce (jak top-jajka biomów)
      minRarityOnBulk: { bulkSize: 10, minRarity: 'epic' },
    },
    dropTable: buildDropTable({
      // PREMIUM: celowo BEZ common (jak top-jajka biomów) → normalna pula = 29
      // (15U/9R/4E/1L z 51). Pełne 51 (z 22 common) jest w zwykłym jajku za muszelki;
      // album i tak leci z osobnej puli HUGE (hugeDropTable), nie z tej tabeli.
      uncommon: 300,
      rare: 450,
      epic: 200,
      legendary: 50,
    }, BIOME_EVENT_BEACH),
    art: { textureKey: 'egg_event_capibara_summer_gold', glowColor: '#FFD24A' },
    enabled: true,
  },
  {
    id: halloween.egg.id,
    name: { pl: 'Jajko Dyniowe', en: 'Pumpkin Egg' },
    cost: { currency: halloween.event.currency.id, amount: halloween.egg.cost },
    biomeId: halloween.event.biomeId,
    eventId: halloween.event.id,
    hugeChance: halloween.egg.hugeChance,
    hugeDropTable: halloween.event.petIds.map((petId) => ({ petId, variant: 'huge', weight: 1 })),
    titanicChance: halloween.egg.titanicChance,
    titanicDropTable: [{ petId: 'pet-event-capibara-halloween-orange', variant: 'titanic', weight: 1 }],
    albumProgress: halloween.egg.albumProgress,
    dropTable: buildDropTable({ common: 500, uncommon: 350, rare: 120, epic: 28, legendary: 2 }, halloween.event.biomeId),
    art: { textureKey: 'egg_event_capibara_halloween', glowColor: '#BDA6E8' },
    enabled: true,
  },
];

/** Mapa pomocnicza id → EggDef. */
export const EGGS_BY_ID: Record<string, EggDef> = Object.fromEntries(
  EGGS.map((e) => [e.id, e]),
);

/**
 * Jajka do listy w sklepie danego biomu.
 *
 * Filtr: `enabled` + dopasowanie biomu. **Jajko eventowe (`eventId`) pokazujemy
 * TYLKO gdy jego event AKTYWNY** (`isEventActive`) — inaczej, gdy gracz zostanie
 * na zapisanym biomie plaży po zakończeniu eventu, sklep pokazywałby jajka, których
 * serwer i tak nie sprzeda (`event_inactive`). Jeden punkt prawdy „czy jajko jest
 * w sprzedaży" — preload/sklep/komentarze mówią to samo (Codex EV-2b-1/2b-3 MED).
 */
export function getActiveEggsForBiome(biomeId: string, now: Date = new Date()): EggDef[] {
  return EGGS.filter((e) => {
    if (!e.enabled) return false;
    if (e.biomeId && e.biomeId !== biomeId) return false;
    if (e.eventId && !isEventActive(e.eventId, now)) return false;
    return true;
  });
}
